import type { SearchKey } from '@schibsted-nmp/search-frontend-clients/quest';
import type { Suggestion } from '@schibsted-nmp/search-frontend-clients/suggest';
import { useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { useAppConfig } from '../AppConfig';
import { countSymbols } from '../common/countSymbols';

const FIVE_SECONDS_IN_MILLISECONDS = 1000 * 5;

export const queryKeys = {
    suggestions: (vars: SuggestionsQueryKeyVars) =>
        [{ scope: 'suggestions', ...vars }] as const,
};

type SuggestionsQueryKeyVars = {
    searchKey: SearchKey | null;
    searchTerm: string | null;
    where: string | null;
};

interface SuggestionsQueryMeta {
    apiUrl: string | null;
    cookieHeader?: string;
}

declare module '@tanstack/react-query' {
    type QueryMeta = SuggestionsQueryMeta;
}

export function useSuggestionsData(searchTerm: string | null) {
    const { apiUrl, searchKey, where } = useAppConfig();

    const isNoTermSearch = (searchTerm ? countSymbols(searchTerm) : 0) < 2;
    const isNoTermLandingPageSearch = where == 'LANDINGPAGE' && isNoTermSearch;

    const isEnabled =
        where !== null && searchTerm !== null && !isNoTermLandingPageSearch;

    const suggestionsResult = useQuery({
        enabled: isEnabled,
        meta: {
            apiUrl,
        },
        placeholderData: !isNoTermLandingPageSearch
            ? (previousData) => previousData
            : undefined,
        staleTime: isNoTermSearch ? 0 : FIVE_SECONDS_IN_MILLISECONDS,
        queryFn: fetchSuggestionsData,
        queryKey: queryKeys.suggestions({
            searchKey,
            searchTerm,
            where,
        }),
    });

    return {
        suggestionsData: suggestionsResult.data ?? null,
    };
}

type SuggestionsQueryKey = ReturnType<(typeof queryKeys)['suggestions']>;

export async function fetchSuggestionsData({
    meta,
    signal,
    queryKey,
}: QueryFunctionContext<SuggestionsQueryKey>): Promise<Suggestion> {
    const [{ searchKey, searchTerm, where }] = queryKey;

    const urlParams = new URLSearchParams({
        searchKey: searchKey ?? '',
        searchTerm: searchTerm ?? '',
        where: where ?? '',
    });

    const { apiUrl, cookieHeader } = meta ?? {};

    const headers: HeadersInit = {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
    };

    if (cookieHeader) {
        headers['Cookie'] = cookieHeader as string;
    }

    const url = new URL(`${apiUrl}/suggest`);
    url.search = urlParams.toString();

    try {
        const response = await fetch(url, {
            credentials: 'same-origin',
            headers,
            signal,
        });
        if (!response.ok) {
            throw new Error(`Got status ${response.status} calling ${url}`);
        }
        return await response.json();
    } catch (err) {
        return Promise.reject(
            new Error(`Error calling ${url}`, { cause: err as Error }),
        );
    }
}
